import { MouseEvent, KeyboardEvent, useState, useEffect } from 'react'
import Map from 'components/Map/Map'
import SideAndMain from 'layouts/SideAndMainLayout/SideAndMainLayout'
import { Button } from 'react-bootstrap'
import { MAX_RADIUS, MIN_RADIUS } from 'redux/features/map/slice'
import AutocompleteInsee from 'components/AutocompleteInsee/AutocompleteInsee'
import RangeSliderDistance from 'components/RangeSlider/RangeSliderDistance/RangeSliderDistance'
import ToggleSwitch from 'components/ToggleSwitch'
import Label from 'components/Label'
import useSearchCircle from 'hooks/useSearchCircle'
import useToggle from 'hooks/useToggle'
import cn from 'classnames'
import useDocumentTitle from 'hooks/useDocumentTitle'
import useEtudierMarche from 'hooks/useEtudierMarche'
import CheckboxGroup from 'components/CheckboxGroup'
import { TypeBien } from 'interfaces/API'
import InputNumberDouble from 'components/InputNumberDouble'
import formatNumber from 'utils/formatNumber'
import ReactLoading from 'react-loader-spinner'
import TripleGrow from 'components/Loading/TripleGrow'
import { Element } from 'react-scroll'
import { Status } from 'redux/features/dvf/slice'
import ResultsDVF from 'components/Layers/ResultsDVF/ResultsDvf'
import CardDVFResult from 'components/Card/Card/CardDVFResult'
import SearchCircle from 'components/SearchCircle/SearchCircle'
import ToastAlert from 'components/ToastAlert'
import Synthese from 'pages/BiensAVendre/components/Synthese'
import pluralizer from 'utils/pluralizer'
import ButtonArrow from 'components/ButtonArrow'
import useTrackRecherche from '../../hooks/useTrackRecherche'
import BeaucoupDeResultats from './components/BeaucoupDeResultats'
import MonthPicker from '../../components/MonthPicker/MonthPicker'
import ButtonIcon from '../../components/ButtonIcon'
import s from '../common.module.scss'

type DateTransaction = 'start' | 'end'

const EtudierMarche = (): JSX.Element => {
  useDocumentTitle('Etude marché')
  const {
    locations,
    typeBien,
    nombrePieces,
    surfaceHabitableMin,
    surfaceHabitableMax,
    surfaceTerrainMin,
    surfaceTerrainMax,
    montantMin,
    montantMax,
    startDate,
    endDate,
    setLocations,
    setTypeDeBien,
    setNombrePieces,
    setSurfaceHabitableMin,
    setSurfaceHabitableMax,
    setSurfaceTerrainMin,
    setSurfaceTerrainMax,
    setMontantMin,
    setMontantMax,
    setStartDate,
    setEndDate,
    onSubmit,
    onEffacerCriteresClick,
    onNouvelleEtudeClick,
    onEditClick,
    onCardDvfClick,
    exportToCsv,
    parcelles,
    statusDvf,
    errorDvf,
    displayMode,
    nextParcelle,
    previousParcelle,
    isNextAvailable,
    isPreviousAvailable,
    placeholderForm,
  } = useEtudierMarche()

  const {
    searchCircle,
    computeSearchCircle,
    resetSearchCircle,
    setSearchCircleRadius,
  } = useSearchCircle()
  const [toggleSearchMode, setToggleSearchMode] = useToggle()
  const [typeMonthPickerActive, setTypeMonthPickerActive] =
    useState<DateTransaction>('start')
  const [showDatePicker, setShowDatePicker] = useState(false)

  const handleToggleLocalisation = (newValue: boolean) => {
    setToggleSearchMode(newValue)
    if (newValue) {
      computeSearchCircle()
    } else {
      resetSearchCircle()
    }
  }

  const onClickTypeOpenDatePicker = (type: DateTransaction) => {
    if (showDatePicker && typeMonthPickerActive === type) {
      setShowDatePicker(false)
      return
    }

    setTypeMonthPickerActive(type)
    setShowDatePicker(true)
  }

  const handleMonthPickerChange = (date: Date | null): void => {
    if (typeMonthPickerActive === 'end') {
      setEndDate(date)
    } else {
      setStartDate(date)
    }
    setShowDatePicker(false)
  }

  const handleRemoveTransactionDate = (
    event: MouseEvent | KeyboardEvent,
    target: DateTransaction
  ): void => {
    event.preventDefault()
    if (target === 'start') {
      setStartDate(null)
    } else {
      setEndDate(null)
    }

    if (startDate === null && endDate === null) {
      setShowDatePicker(false)
    }
  }

  useEffect(() => {
    if (showDatePicker && startDate === null && endDate === null) {
      setShowDatePicker(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  useTrackRecherche({ status: statusDvf, event: 'rechercheEtudierMarche' })

  return (
    <SideAndMain
      aside={
        <>
          <ToastAlert show={statusDvf === Status.Failed} variant="danger">
            {errorDvf}
          </ToastAlert>
          {/* Formulaire de recherche "Etudier votre marché" */}
          <div
            className={cn(s.formulaire, {
              [s.hide]: displayMode === 'synthese',
            })}
          >
            <div className={s.localisation}>
              <div className={s.flexBetween}>
                <Label label="Localisation" />
                <Button
                  className={s.underline}
                  variant="link"
                  onClick={onEffacerCriteresClick}
                >
                  Effacer les critères
                </Button>
              </div>
              <ToggleSwitch
                className={s.toggleSwitch}
                value={toggleSearchMode}
                onChange={handleToggleLocalisation}
                labelLeft="Localiser par ville(s)"
                labelRight="Localiser avec un rayon"
              />
              {!toggleSearchMode && (
                <AutocompleteInsee
                  locations={locations}
                  setLocations={setLocations}
                />
              )}
              {toggleSearchMode && (
                <>
                  <p>
                    Déplacez-vous sur la carte pour modifier la zone de votre
                    rayon.
                    <br />
                    Puis choisissez l&apos;étendue de votre rayon ci-dessous :
                  </p>
                  {searchCircle && (
                    <RangeSliderDistance
                      min={MIN_RADIUS}
                      max={MAX_RADIUS}
                      value={searchCircle.radius}
                      onChange={setSearchCircleRadius}
                    />
                  )}
                </>
              )}
            </div>
            <div className={s.typeDeBien}>
              <Label label="Type de bien : " />
              <CheckboxGroup
                id="etudierMarche-TypeDeBien"
                onChange={setTypeDeBien}
                value={typeBien}
                options={[
                  {
                    label: 'Maison',
                    value: TypeBien.Maison,
                  },
                  {
                    label: 'Appartement',
                    value: TypeBien.Appartement,
                  },
                  {
                    label: 'Terrain',
                    value: TypeBien.Terrain,
                  },
                ]}
              />
            </div>
            <div className={s.nombrePiece}>
              <Label label="Nombre de pièces : " />
              <CheckboxGroup
                id="etudierMarche-nombrePieces"
                onChange={setNombrePieces}
                value={nombrePieces}
                options={[
                  {
                    label: '1 pièce',
                    value: 1,
                  },
                  {
                    label: '2 pièces',
                    value: 2,
                  },
                  {
                    label: '3 pièces',
                    value: 3,
                  },
                  {
                    label: '4 pièces',
                    value: 4,
                  },
                  {
                    label: '5 pièces et plus',
                    value: 5,
                  },
                ]}
              />
            </div>
            <div className={s.surfaceHabitable}>
              <Label label="Surface habitable : " />
              <InputNumberDouble
                labelStart="Entre"
                labelMid="et"
                labelEnd="m²"
                leftInput={{
                  value: formatNumber(surfaceHabitableMin),
                  onChange: setSurfaceHabitableMin,
                }}
                rightInput={{
                  value: formatNumber(surfaceHabitableMax),
                  onChange: setSurfaceHabitableMax,
                  placeholder: String(placeholderForm.surfaceHabitableMax),
                }}
              />
            </div>
            <div className={s.surfaceTerrain}>
              <Label label="Surface du terrain : " />
              <InputNumberDouble
                labelStart="Entre"
                labelMid="et"
                labelEnd="m²"
                leftInput={{
                  value: formatNumber(surfaceTerrainMin),
                  onChange: setSurfaceTerrainMin,
                }}
                rightInput={{
                  value: formatNumber(surfaceTerrainMax),
                  onChange: setSurfaceTerrainMax,
                  placeholder: String(placeholderForm.surfaceTerrainMax),
                }}
              />
            </div>
            <div className={s.montant}>
              <Label label="Montant : " />
              <InputNumberDouble
                labelStart="Entre"
                labelMid="et"
                labelEnd="€"
                leftInput={{
                  value: formatNumber(montantMin),
                  onChange: setMontantMin,
                }}
                rightInput={{
                  value: formatNumber(montantMax),
                  onChange: setMontantMax,
                  placeholder: String(
                    placeholderForm.montantTransactionMontantMax
                  ),
                }}
              />
            </div>
            <div className={s.dateTransaction}>
              <Label label="Date de la transaction : " />
              <div className={s.blockButtonsTransactionDate}>
                <div>
                  <Button
                    className={s.monthTransactionDateBtn}
                    onClick={() => {
                      onClickTypeOpenDatePicker('start')
                    }}
                    variant="none"
                  >
                    <p>Date de début</p>
                    <div>
                      <span>
                        {startDate?.toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        }) ?? '-'}
                      </span>
                    </div>
                  </Button>
                  {startDate && (
                    <div
                      tabIndex={0}
                      role="button"
                      className={s.buttonReset}
                      onClick={(e) => handleRemoveTransactionDate(e, 'start')}
                      onKeyUp={(e) => handleRemoveTransactionDate(e, 'start')}
                    >
                      <ButtonIcon icon="cross" size="xs" />
                    </div>
                  )}
                </div>
                <div>
                  <Button
                    className={s.monthTransactionDateBtn}
                    onClick={() => {
                      onClickTypeOpenDatePicker('end')
                    }}
                    variant="none"
                  >
                    <p>Date de fin</p>
                    <div>
                      <span>
                        {endDate?.toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        }) ?? '-'}
                      </span>
                    </div>
                  </Button>
                  {endDate && (
                    <div
                      tabIndex={0}
                      role="button"
                      className={s.buttonReset}
                      onClick={(e) => handleRemoveTransactionDate(e, 'end')}
                      onKeyUp={(e) => handleRemoveTransactionDate(e, 'end')}
                    >
                      <ButtonIcon icon="cross" size="xs" />
                    </div>
                  )}
                </div>
              </div>
              <MonthPicker
                type={typeMonthPickerActive}
                show={showDatePicker}
                startDate={
                  typeMonthPickerActive === 'start' ? startDate : endDate
                }
                onChange={(date: Date | null) => handleMonthPickerChange(date)}
              />
            </div>

            <div className={s.sectionFooter}>
              {statusDvf === Status.ShowParcelleResults &&
                !parcelles.length && (
                  <p className={s.noResult}>
                    Aucun résultat ne correspond à votre recherche.
                  </p>
                )}
              <Button
                id="page-etudier-marche-btn-rechercher"
                className="mb-4"
                variant="primary"
                size="lg"
                disabled={statusDvf === Status.Pending}
                onClick={onSubmit}
              >
                {statusDvf === Status.Pending ? (
                  <ReactLoading type="Oval" color="#fff" height="2.4rem" />
                ) : (
                  'Rechercher'
                )}
              </Button>
              <Button
                className={s.underline}
                variant="link"
                onClick={onNouvelleEtudeClick}
              >
                Nouvelle étude
              </Button>
            </div>
          </div>
          {/* Synthese du formulaire */}
          <div
            className={cn(s.sectionFiltresEtNavigation, {
              [s.hide]: displayMode === 'normal',
            })}
          >
            <div className={s.syntheseControls}>
              <Button
                variant="link"
                className={s.underline}
                onClick={onEditClick}
              >
                Modifier
              </Button>
              <Button
                variant="link"
                className={cn(s.underline)}
                onClick={onNouvelleEtudeClick}
              >
                Nouvelle étude
              </Button>
            </div>
            <Synthese
              locations={locations}
              typeBien={typeBien}
              surfaceHabitableMin={surfaceHabitableMin}
              surfaceHabitableMax={surfaceHabitableMax}
              surfaceTerrainMin={surfaceTerrainMin}
              surfaceTerrainMax={surfaceTerrainMax}
              setSurfaceHabitableMin={setSurfaceHabitableMin}
              setSurfaceHabitableMax={setSurfaceHabitableMax}
              setSurfaceTerrainMin={setSurfaceTerrainMin}
              setSurfaceTerrainMax={setSurfaceTerrainMax}
              submitForm={onSubmit}
            />
            {statusDvf === Status.ShowParcelleResults && (
              <div className={s.resultAndNavigation}>
                <div className={s.resultCount}>
                  <p>
                    <span
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: pluralizer(
                          [
                            '<b>Aucun résultat</b> ne correspond à votre recherche',
                            '<b>{{count}} résultat</b> correspond à votre recherche',
                            '<b>{{count}} résultats</b> correspondent à votre recherche',
                          ],
                          parcelles.length
                        ),
                      }}
                    />
                  </p>
                  {!!parcelles.length && (
                    <p>Visitez ces biens grâce aux boutons ci-dessous :</p>
                  )}
                </div>
                {parcelles.length > 1 && (
                  <div className={s.resultNavigation}>
                    <ButtonArrow
                      size="xs"
                      className={s.arrowLeft}
                      direction="left"
                      onClick={previousParcelle}
                      disabled={!isPreviousAvailable}
                      label={{
                        text: 'Précédent',
                        position: 'after',
                      }}
                    />
                    <ButtonArrow
                      size="xs"
                      className={s.arrowRight}
                      direction="right"
                      onClick={nextParcelle}
                      disabled={!isNextAvailable}
                      label={{
                        text: 'Suivant',
                        position: 'before',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Les resultats DVF */}
          <div
            id="mutation-list-result"
            className={cn(s.parcelleListResult, {
              [s.hide]: displayMode === 'normal',
            })}
          >
            <div className={s.wrapper}>
              {/* Display the loader while the modal BeaucoupDeResultats is open */}
              {statusDvf !== Status.ShowParcelleResults &&
                statusDvf !== Status.Idle && (
                  <TripleGrow className={s.loading} />
                )}
              {statusDvf === Status.ShowParcelleResults &&
                parcelles.length > 0 &&
                parcelles.map((parcelleDvf, index) => (
                  <Element
                    name={parcelleDvf.parcelleUid}
                    key={parcelleDvf.parcelleUid}
                    className={s.cardParcelleWrapper}
                  >
                    <CardDVFResult
                      key={parcelleDvf.parcelleUid}
                      parcelleDvf={parcelleDvf}
                      onClick={onCardDvfClick}
                      count={{
                        index,
                        total: parcelles.length,
                      }}
                    />
                  </Element>
                ))}
              <BeaucoupDeResultats />
            </div>
          </div>
          <div
            className={cn(s.bannerExportListing, {
              [s.hide]: displayMode === 'normal',
            })}
          >
            <Button
              variant="primary"
              onClick={exportToCsv}
              disabled={
                statusDvf !== Status.ShowParcelleResults ||
                parcelles.length === 0
              }
            >
              Exporter le listing
            </Button>
          </div>
        </>
      }
      main={
        <Map>
          <>
            <ResultsDVF />
            {searchCircle && displayMode === 'normal' && <SearchCircle />}
          </>
        </Map>
      }
    />
  )
}

export default EtudierMarche
